import { useEffect, useCallback } from 'react';
import { useMessageBus } from './useMessageBus';
import {
  useCrmMessagingContext,
  CrmMessagingContextType,
} from './CrmMessagingContext';

import { TopicName } from './types/MessageTopics';

import {
  OnMessageConfig,
  MessageEnvelope,
  CallbackFor,
  EnvelopeFor,
} from './types/MessageBusTypes';
import { useRegisterHandler } from './MessageBusSpyContext';

function getMessageSource(envelope: MessageEnvelope = {}) {
  return envelope.sourceId;
}

function getMessageTarget(envelope: MessageEnvelope = {}) {
  return envelope.targetId;
}

function getCurrentMessagingId(messageContext?: CrmMessagingContextType) {
  if (!messageContext) {
    return undefined;
  }
  return messageContext.messagingId;
}

function messageCameFromCurrentMessenger(
  messageContext?: CrmMessagingContextType,
  envelope: MessageEnvelope = {}
): boolean {
  const currentMessagingId = getCurrentMessagingId(messageContext);
  const messageSource = getMessageSource(envelope);
  return !!messageSource && messageSource === currentMessagingId;
}

export function useOnCrmMessageTopic<
  Name extends TopicName,
  Callback extends CallbackFor<Name>,
>(topic: Name, onTopic: Callback, config?: OnMessageConfig) {
  const messageBus = useMessageBus();
  const messagingContext = useCrmMessagingContext();
  const registerHandler = useRegisterHandler();
  const skip = config && config.skip;

  const cbWrapper = useCallback(
    (envelopeData: EnvelopeFor<Name>) => {
      const { data, envelope } = envelopeData;

      if (
        config &&
        config.ignoreLocal &&
        messageCameFromCurrentMessenger(messagingContext, envelope)
      ) {
        return;
      }

      const target = getMessageTarget(envelope);
      if (target && target !== getCurrentMessagingId(messagingContext)) {
        return;
      }

      onTopic(data, envelope);
    },
    [onTopic, messagingContext, config]
  );

  useEffect(() => {
    if (!messageBus || skip) {
      return () => {};
    }

    // Register handler with spy context if available
    registerHandler?.(topic);

    const unsubscribe = messageBus.subscribe(topic, cbWrapper);
    return () => {
      unsubscribe();
    };
  }, [messageBus, topic, cbWrapper, skip, registerHandler]);
}
